import { useCallback, useEffect, useRef, useState } from "react";
import { clamp, lerp, fmtTime, type Col } from "./util";
import { TUNING, TUNING_DEFAULTS, KNOBS, CAR } from "./tuning";
import { TRACKS, tracePath, type Segment } from "./tracks";
import { drawPlayerCar, drawTrafficCar, drawHazard, quad, checker, PAINTS } from "./sprites";
import { createEngineAudio } from "./audio";
import { DIFFS, createRivals, buildAllowedSpeeds, updateRivals, type Rival, type Hazard } from "./ai";
import { MEDAL_ICON, awardChallenges, loadChallenges, timeTargets, type RaceStats } from "./missions";
import { CSS } from "./styles";

/**
 * APEX — a fastest-lap arcade racer (Bryce's "simple competition" entry).
 *
 * Pole Position / OutRun-style pseudo-3D: a curved circuit projected toward the horizon,
 * sprite car at the bottom, radial tach. Driving is "arcade + real shifting": gears, RPM,
 * torque curve, redline fuel-cut — but NO clutch or stalling. Race N laps against three
 * AI rivals (EASY/MED/HARD) plus slow traffic; fastest single lap is the record that
 * persists.
 *
 * One imperative canvas + rAF loop in a single useEffect; React owns structure. The fast
 * HUD is written straight to the DOM via data-el refs. Sibling modules:
 *   tuning.ts (feel knobs + car), tracks.ts (circuits + themes), sprites.ts (drawing),
 *   ai.ts (rivals), audio.ts (engine synth), styles.ts (CSS).
 */
export function BryceLapRacer() {
  const rootRef = useRef<HTMLDivElement>(null);
  const roadRef = useRef<HTMLCanvasElement>(null);
  const tachRef = useRef<HTMLCanvasElement>(null);
  const mapRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void wrapRef.current?.requestFullscreen({ navigationUI: "hide" });
  }, []);

  // The race view goes fullscreen on its own; F toggles it. The canvases refit on the resize event.
  useEffect(() => {
    const onChange = () => {
      setFullscreen(document.fullscreenElement === wrapRef.current);
      window.dispatchEvent(new Event("resize"));
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "f" && !e.metaKey && !e.ctrlKey && !e.altKey && !e.repeat) toggleFullscreen();
    };
    document.addEventListener("fullscreenchange", onChange);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      window.removeEventListener("keydown", onKey);
    };
  }, [toggleFullscreen]);

  useEffect(() => {
    const root = rootRef.current;
    const roadCanvas = roadRef.current;
    const tachCanvas = tachRef.current;
    const mapCanvas = mapRef.current;
    if (!root || !roadCanvas || !tachCanvas || !mapCanvas) return;

    const T = TUNING;
    const car = CAR;
    const ratio = (g: number) => car.ratios[g] ?? 0;
    // The player SPRITE sits this far ahead of the camera — where the projection's near
    // plane meets the bottom of the screen. ALL collision happens on that plane, so
    // things hit exactly where they appear to be, not at the invisible camera position.
    const PLAYER_Z = T.CAMERA_DEPTH * T.CAMERA_HEIGHT * 1.15;

    // current track + difficulty (selectable on the start screen); loadTrack() fills in
    let trackIdx = 0;
    let track = TRACKS[0]!;
    let theme = track.theme;
    let road: Segment[] = [];
    let N = 0;
    let trackLen = 0;
    // mode 0 = TIME TRIAL (the same track.laps timed race, just no rivals);
    // 1..3 = VS the AI pack at DIFFS[mode-1]
    const MODES = [
      { id: "tt", name: "TIME TRIAL" },
      ...DIFFS.map((d) => ({ ...d, name: "VS · " + d.name })),
    ];
    let modeIdx = 2; // MED default
    const raceDiff = () => (modeIdx > 0 ? DIFFS[modeIdx - 1]! : null);
    // race length lives on the track (tracks.ts); TUNING.RACE_LAPS is just the fallback
    const raceLaps = () => track.laps ?? T.RACE_LAPS;
    let allowedSpeeds: number[] = []; // AI braking map for current track + difficulty

    // roadside hazards (trees/cacti/rocks — deterministic clusters), scenery so off-road
    // feels alive. Only beside straighter segments: on a sharp curve a roadside billboard
    // projects over the road at the vanishing point (can't be occluded there), so skip.
    // track.hazards.density scales spawn rate: 1 ≈ every ~7 straight segments, 0 = none.
    const treesBySeg = new Map<number, number[]>();
    function buildTrees() {
      treesBySeg.clear();
      const density = track.hazards.density;
      if (density <= 0) return;
      for (let i = 0; i < N; i++) {
        const h = (i * 2654435761) >>> 0; // cheap stable hash → same layout every run
        if (h % 1000 < 143 * density && Math.abs(road[i]!.curve) < 2) {
          const side = h & 8 ? 1 : -1;
          const off = side * (2.8 + ((h >> 4) % 100) / 100 * 2.4); // 2.8..5.2 — well clear of the road
          treesBySeg.set(i, [off]);
        }
      }
    }

    // slow traffic to dodge / bump — now on a 3-lane road
    const LANES = [-0.55, 0, 0.55];
    interface Traffic {
      pos: number; x: number; targetX: number; switchIn: number;
      speed: number; color: Col; variant: number; cool: number;
    }
    const TRAFFIC_COLORS: Col[] = [[63, 127, 214], [224, 165, 58], [122, 82, 201], [74, 169, 108], [217, 79, 106]];
    let traffic: Traffic[] = [];
    let rivals: Rival[] = [];
    let treeCool = 0;
    let impactFlash = 0; // red vignette + judder on contact (instead of a text popup)
    function initTraffic() {
      traffic = [];
      // track.traffic is a DENSITY (cars per 100k world units) so track length doesn't
      // skew it; TRAFFIC_MUL is the dev-panel dial — 0 empties the road (clean testing)
      const count = Math.round((trackLen / 100000) * track.traffic * T.TRAFFIC_MUL);
      for (let i = 0; i < count; i++) {
        const lane = LANES[i % 3]! + ((i * 37) % 7) / 100 - 0.03;
        traffic.push({
          pos: (trackLen * (i + 0.5)) / count,
          x: lane, targetX: lane, switchIn: 2 + (i * 1.7) % 6,
          speed: 64 + (i % 4) * 13,
          color: TRAFFIC_COLORS[i % TRAFFIC_COLORS.length]!,
          variant: i % 3,
          cool: 0,
        });
      }
    }
    function initRivals() {
      rivals = raceDiff() ? createRivals(trackLen, PLAYER_Z) : [];
    }

    // signed distance from a world position to the player-sprite plane
    function gapToPlane(p: number) {
      const plane = S.position + PLAYER_Z;
      let g = (((p - plane) % trackLen) + trackLen) % trackLen;
      if (g > trackLen / 2) g -= trackLen;
      return g;
    }
    // "hit" when inside the car-length window — or when the closing speed stepped clean
    // over the window in one frame (no tunneling through cars at 200 mph).
    // Window is asymmetric: a bit more reach ahead (your car's nose extends up-screen
    // past the sprite) than behind (a car behind the plane is sliding off the bottom
    // edge beside you). Lateral 0.22 is calibrated to DRAWN separation, not road units:
    // steering slides the world under the camera while the player sprite barely moves,
    // so sprites visually touch at ~0.21 offset — a wider box hits through visible air.
    // "near" = crossed the plane a whisker outside the hitbox — that's a NEAR MISS,
    // and near misses are how the NOS tank fills.
    // `ahead` = they were in front of my nose when it happened (I hit THEM); false =
    // I was hit from behind — direction decides who pays for the contact.
    function planeInteraction(pos: number, prevPos: number, x: number, dt: number): { act: "hit" | "near"; ahead: boolean } | null {
      const gap = gapToPlane(pos);
      const prevGap = gapToPlane(prevPos) + S.speed * T.POS_K * dt;
      const crossed = (prevGap > 0) !== (gap > 0) && Math.abs(prevGap - gap) < trackLen / 4;
      const dx = Math.abs(x - S.playerX);
      const ahead = crossed ? prevGap > 0 : gap > 0;
      if ((crossed || (gap < 150 && gap > -70)) && dx < 0.22) return { act: "hit", ahead };
      if (crossed && dx < 0.5 && S.speed > 30) return { act: "near", ahead };
      return null;
    }
    function nearMiss() {
      S.boost = Math.min(1, S.boost + T.BOOST_FILL);
      nosFlashUntil = gameTime + 0.9;
    }

    function updateTraffic(dt: number) {
      for (const t of traffic) {
        const prevPos = t.pos;
        t.pos = (t.pos + t.speed * T.POS_K * dt) % trackLen;
        if (t.cool > 0) t.cool -= dt;
        const gap = gapToPlane(t.pos);
        // lane switching: every few seconds pick a new lane and drift over to it —
        // but never swerve while close to the player's plane (no cheap sideswipes)
        t.switchIn -= dt;
        if (t.switchIn <= 0) {
          if (Math.abs(gap) > 900) t.targetX = LANES[Math.floor(Math.random() * 3)]! + (Math.random() - 0.5) * 0.08;
          t.switchIn = 3 + Math.random() * 5;
        }
        t.x += (t.targetX - t.x) * Math.min(1, dt * 1.1);
        if (S.phase !== "racing") continue;
        const act = planeInteraction(t.pos, prevPos, t.x, dt);
        if (act?.act === "hit" && t.cool <= 0) {
          const dir = Math.sign(S.playerX - t.x) || 1;
          const closing = S.speed - t.speed;
          if (act.ahead && closing > 25) {
            // I ran into them — I lose my drive; my fault, breaks my clean run
            S.speed *= 0.42;
            S.driftVel += dir * 1.8;
            S.contacts += 1;
            impactFlash = 1;
          } else if (!act.ahead && closing < -25) {
            // they ran into ME — a shove from behind, not a wall; they eat the loss
            S.speed = Math.max(S.speed, t.speed * 0.75);
            S.driftVel += dir * 0.9;
            t.speed *= 0.55;
            impactFlash = 0.55;
          } else {
            // drifted into a car alongside — scrape, shove apart, carry on
            S.speed *= 0.9;
            S.driftVel += dir * 1.2;
            t.targetX = clamp(t.x - dir * 0.35, -0.55, 0.55);
            impactFlash = 0.4;
          }
          t.cool = 0.8;
        } else if (act?.act === "near") nearMiss();
      }
      // trees only bite once you're off the road — measured on the same sprite plane
      if (treeCool > 0) treeCool -= dt;
      if (S.phase === "racing" && Math.abs(S.playerX) > 1 && treeCool <= 0) {
        // d 0..1 spans ±1 segment around the plane (a tree sits at its segment's start);
        // lateral 0.16 ≈ the DRAWN tree (thin trunk + canopy) actually brushing the car —
        // any wider and you get TREE'd by visibly empty grass
        const pseg = Math.floor((S.position + PLAYER_Z) / T.SEG_LEN) % N;
        for (let d = 0; d <= 1; d++) {
          const trees = treesBySeg.get(((pseg + d) % N + N) % N);
          if (!trees) continue;
          for (const ox of trees) {
            if (Math.abs(ox - S.playerX) < 0.16) {
              S.speed *= 0.2; // a tree is a wall — it nearly stops you
              S.driftVel += (Math.sign(S.playerX - ox) || 1) * 1.6;
              treeCool = 0.6;
              impactFlash = 1;
              S.contacts += 1;
            }
          }
        }
      }
    }

    function playerTotal() { return S.lap * trackLen + S.position + PLAYER_Z; }
    function playerPlace() {
      const total = playerTotal();
      let p = 1;
      for (const r of rivals) if (r.lap * trackLen + r.pos > total) p++;
      return p;
    }
    function runRivals(dt: number) {
      const diff = raceDiff();
      if (!diff || !rivals.length) return;
      const hazards: Hazard[] = [
        ...traffic.map((t) => ({ pos: t.pos, x: t.x, speed: t.speed })),
        ...rivals.map((r) => ({ pos: r.pos, x: r.x, speed: r.speed })),
        { pos: S.position + PLAYER_Z, x: S.playerX, speed: S.speed },
      ];
      updateRivals(rivals, dt, {
        road, segLen: T.SEG_LEN, trackLen, posK: T.POS_K,
        allowed: allowedSpeeds, diff, paceMul: T.AI_PACE,
        playerTotal: playerTotal(), hazards,
      });
      // rival ↔ player contact — THREE kinds, split by direction AND closing speed:
      //   rear-end (them ahead, you 25+ km/h faster): your fault, you lose your drive
      //   punt (you ahead, them 25+ km/h faster): their fault, you're shoved forward
      //   door-to-door rub (similar speeds, side by side): a racing incident — both
      //     scrub a little and get pushed apart; nobody's race ends over paint swap
      for (const r of rivals) {
        const act = planeInteraction(r.pos, r.prevPos, r.x, dt);
        if (act?.act === "hit" && r.cool <= 0) {
          const dir = Math.sign(S.playerX - r.x) || 1;
          const closing = S.speed - r.speed;
          if (act.ahead && closing > 25) {
            S.speed *= 0.55;
            S.driftVel += dir * 1.6;
            r.speed *= 0.9;
            S.contacts += 1; // my fault — breaks the clean run
            impactFlash = 1;
          } else if (!act.ahead && closing < -25) {
            S.speed = Math.max(S.speed, r.speed * 0.8); // punted forward, blameless
            S.driftVel += dir * 0.9;
            r.speed *= 0.68;
            impactFlash = 0.55;
          } else {
            S.speed *= 0.93;
            r.speed *= 0.94;
            S.driftVel += dir * 1.1;
            impactFlash = 0.4; // rubbin' is racin' — doesn't count as a contact
          }
          r.x = clamp(r.x - dir * 0.3, -0.5, 0.5);
          r.cool = 0.8;
        }
        // NB: rivals deliberately give NO near-miss NOS — pacing alongside a
        // similar-speed CPU would farm the tank for free. Traffic only.
      }
    }

    const S = {
      phase: "start" as "start" | "countdown" | "racing" | "finished",
      rpm: car.idle, speed: 0, gear: 1,
      gas: 0, brake: 0, throttle: 0, position: 0, playerX: 0, steerVel: 0, driftVel: 0,
      lap: 0, lapTime: 0, lastLap: 0, bestLap: 0, raceTime: 0, raceBest: 0, fuelCut: false,
      boost: 0, boosting: false, contacts: 0, offroadS: 0,
    };
    let nosFlashUntil = 0; // NOS bar glows briefly when a near-miss fills it
    // race-gap trend (the top chip): sample the gap every ~0.6s so the gaining/losing
    // color reflects real movement, not frame-to-frame jitter
    let gapTargetName = "";
    let gapSampleT = 0;
    let gapSampleV = 0;
    let gapTrend = 0;
    let shiftToastUntil = 0; // small toast above the dash (perfect shift / launch / wheelspin)
    let bogUntil = 0; // wheelspin from a botched launch — throttle does almost nothing
    function toast(text: string, warn = false) {
      shiftToastEl.textContent = text;
      shiftToastEl.classList.toggle("warn", warn);
      shiftToastEl.classList.add("show");
      shiftToastUntil = gameTime + 1.6;
    }
    let bestSplits: number[] | null = null;
    let curSplits: number[] = [];
    let lastSeg = -1;

    function speedToRPM(speedKmh: number, gear: number) {
      const mps = Math.abs(speedKmh) / 3.6;
      const wheelRevPerSec = mps / (2 * Math.PI * car.wheelR);
      return wheelRevPerSec * Math.abs(ratio(gear)) * car.final * 60;
    }
    function torqueAt(rpm: number) {
      const t = 1 - Math.pow((rpm - car.tqPeak) / car.tqWidth, 2) * 0.55;
      return Math.max(0.4, Math.min(1, t));
    }

    // ---------- persistence ----------
    const bestKeyFor = (trackId: string) => `lapracer.best.${car.id}.${trackId}`;
    function loadBest() {
      try {
        let v = localStorage.getItem(bestKeyFor(track.id));
        if (!v && track.id === "laguna") v = localStorage.getItem("lapracer.best." + car.id); // pre-track-select saves
        if (!v) return null;
        const o = JSON.parse(v);
        if (o && typeof o.time === "number" && Array.isArray(o.splits)) {
          // a record whose splits don't span this track was set on an OLD layout —
          // its time is meaningless here and its splits would freeze the live delta
          // partway around the lap. Ignore it; the next finished lap overwrites it.
          if (o.splits.length < N - 10 || o.splits.length > N) return null;
          return o as { time: number; splits: number[] };
        }
      } catch { /* storage may be unavailable */ }
      return null;
    }
    function saveBest(time: number, splits: number[]) {
      try { localStorage.setItem(bestKeyFor(track.id), JSON.stringify({ time, splits })); } catch { /* ignore */ }
    }
    // best TOTAL race time, stored per lap-count so the RACE_LAPS knob can't corrupt it
    const raceBestKeyFor = (trackId: string) => `lapracer.racebest.${car.id}.${trackId}`;
    const readJSON = (k: string) => { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; } };
    function saveRaceBest(time: number) {
      try {
        const rb = (readJSON(raceBestKeyFor(track.id)) ?? {}) as Record<string, number>;
        const k = String(raceLaps());
        if (!(typeof rb[k] === "number") || time < rb[k]!) {
          rb[k] = time;
          localStorage.setItem(raceBestKeyFor(track.id), JSON.stringify(rb));
        }
      } catch { /* ignore */ }
    }
    // segment count per track, for validating stored records against the CURRENT layout
    const TRACK_SEGS = TRACKS.map((tk) => tk.build().length);
    function bestTimesFor(trackIdx2: number): { lap: number | null; race: number | null } {
      const tk = TRACKS[trackIdx2]!;
      let o = readJSON(bestKeyFor(tk.id));
      if (!o && tk.id === "laguna") o = readJSON("lapracer.best." + car.id);
      // same staleness rule as loadBest: a record from an old layout doesn't count
      const segs = TRACK_SEGS[trackIdx2]!;
      const valid = o && typeof o.time === "number" && Array.isArray(o.splits)
        && o.splits.length >= segs - 10 && o.splits.length <= segs;
      const lap = valid ? (o.time as number) : null;
      // race bests are keyed by lap count — use THAT track's laps, not the selected one
      const rb = readJSON(raceBestKeyFor(tk.id)) as Record<string, number> | null;
      const race = rb && typeof rb[String(tk.laps)] === "number" ? rb[String(tk.laps)]! : null;
      return { lap, race };
    }

    // ---------- DOM handles ----------
    const q = <E extends HTMLElement = HTMLElement>(sel: string) => root.querySelector(sel) as E;
    const speedEl = q('[data-el="speed"]');
    const rpmEl = q('[data-el="rpm"]');
    const gearEl = q('[data-el="gear"]');
    const lapTimeEl = q('[data-el="lapTime"]');
    const bestEl = q('[data-el="best"]');
    const lastEl = q('[data-el="last"]');
    const lapNumEl = q('[data-el="lapNum"]');
    const posEl = q('[data-el="pos"]');
    const deltaEl = q('[data-el="delta"]');
    const deltaMainEl = q('[data-el="deltaMain"]');
    const deltaSubEl = q('[data-el="deltaSub"]');
    const fillGas = q('[data-el="fillGas"]');
    const fillBrake = q('[data-el="fillBrake"]');
    const bannerEl = q('[data-el="banner"]');
    const soundBtn = q('[data-el="soundBtn"]');
    const startEl = q('[data-el="start"]');
    const startBtn = q('[data-el="startBtn"]');
    const resultsEl = q('[data-el="results"]');
    const resPosRowEl = q('[data-el="resPosRow"]');
    const resPosEl = q('[data-el="resPos"]');
    const resTotalEl = q('[data-el="resTotal"]');
    const resBestEl = q('[data-el="resBest"]');
    const resNoteEl = q('[data-el="resNote"]');
    const resMedalsEl = q('[data-el="resMedals"]');
    const missionRowEl = q('[data-el="missionRow"]');
    const fillNos = q('[data-el="fillNos"]');
    const chalEl = q('[data-el="chal"]');
    const chTarmacEl = q('[data-el="chTarmac"]');
    const chCleanEl = q('[data-el="chClean"]');
    const chWinEl = q('[data-el="chWin"]');
    const chTimeEl = q('[data-el="chTime"]');
    const devEl = q('[data-el="dev"]');
    const devRowsEl = q('[data-el="devRows"]');
    const devResetBtn = q('[data-el="devReset"]');
    const devResetRecBtn = q('[data-el="devResetRecords"]');
    const diffsEl = q('[data-el="diffs"]');
    const shiftToastEl = q('[data-el="shiftToast"]');

    // ---------- track picker (start screen) ----------
    const tracksEl = q('[data-el="tracks"]');
    const cards = TRACKS.map((tk, i) => {
      const card = document.createElement("button");
      card.type = "button";
      card.className = "track-card";
      const cv = document.createElement("canvas");
      const nm = document.createElement("span");
      nm.className = "tname"; nm.textContent = tk.name;
      const tg = document.createElement("span");
      tg.className = "ttag"; tg.textContent = tk.tagline;
      const dots = document.createElement("span");
      dots.className = "mdots";
      for (const key of ["tarmac", "clean", "time", "pack"]) {
        const d = document.createElement("i");
        d.className = "mdot"; d.dataset.k = key;
        dots.appendChild(d);
      }
      const tb = document.createElement("span");
      tb.className = "tbest";
      card.append(cv, nm, tg, dots, tb);
      card.addEventListener("click", () => { if (S.phase === "start") loadTrack(i); });
      tracksEl.appendChild(card);
      // draw the loop preview once
      const pw = 92, ph = 56, dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.width = pw * dpr; cv.height = ph * dpr;
      cv.style.width = pw + "px"; cv.style.height = ph + "px";
      const pctx = cv.getContext("2d")!;
      pctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const pts = tracePath(tk.build(), pw, ph, 6);
      pctx.beginPath();
      pctx.moveTo(pts[0]!.x, pts[0]!.y);
      for (const p of pts) pctx.lineTo(p.x, p.y);
      pctx.closePath();
      pctx.lineJoin = "round"; pctx.lineWidth = 2.5; pctx.strokeStyle = "rgba(255,255,255,.92)"; pctx.stroke();
      pctx.fillStyle = "#ffe07a";
      pctx.beginPath(); pctx.arc(pts[0]!.x, pts[0]!.y, 2.2, 0, Math.PI * 2); pctx.fill();
      return card;
    });
    function loadTrack(i: number) {
      trackIdx = ((i % TRACKS.length) + TRACKS.length) % TRACKS.length;
      track = TRACKS[trackIdx]!;
      theme = track.theme;
      road = track.build();
      N = road.length;
      trackLen = N * T.SEG_LEN;
      buildTrees();
      initTraffic();
      initRivals();
      applyDiff();
      buildMapPath();
      S.position = 0; S.lapTime = 0; S.lap = 0; S.lastLap = 0;
      const b = loadBest();
      bestSplits = b ? b.splits : null;
      curSplits = []; lastSeg = -1;
      S.bestLap = b ? b.time : 0;
      bestEl.textContent = S.bestLap ? fmtTime(S.bestLap) : "--:--.---";
      lastEl.textContent = "--:--.---";
      cards.forEach((c, ci) => c.classList.toggle("active", ci === trackIdx));
      refreshMedalUI();
    }

    // challenge dots + best times on every track card + the selected track's strip
    const fmtShort = (s: number | null) => (s == null ? "--" : fmtTime(s).replace(/(\.\d)\d+$/, "$1"));
    function refreshMedalUI() {
      const save = loadChallenges();
      cards.forEach((card, i) => {
        const c = save[TRACKS[i]!.id] ?? {};
        card.querySelectorAll<HTMLElement>(".mdot").forEach((d) => {
          const k = d.dataset.k!;
          d.dataset.medal = k === "time" || k === "pack"
            ? (c[k] ?? "")
            : c[k as "tarmac" | "clean"] ? "done" : "";
        });
        const bt = bestTimesFor(i);
        const tb = card.querySelector<HTMLElement>(".tbest");
        if (tb) {
          tb.textContent = `⏱ ${fmtShort(bt.lap)} · 🏁 ${fmtShort(bt.race)}`;
          tb.title = "best lap · best race";
        }
      });
      const c = save[track.id] ?? {};
      const [g, s, b] = timeTargets(track.medalLaps, raceLaps());
      const items: { txt: string; has: boolean; title: string }[] = [
        { txt: `${c.tarmac ? "✓" : "○"} TARMAC ONLY`, has: !!c.tarmac, title: "finish without going off-road" },
        { txt: `${c.clean ? "✓" : "○"} NO CONTACT`, has: !!c.clean, title: "finish without hitting anything" },
        {
          txt: `${c.time ? MEDAL_ICON[c.time] : "○"} UNDER ${fmtTime(b)}`, has: !!c.time,
          title: `finish the race under 🥉 ${fmtTime(b)} · 🥈 ${fmtTime(s)} · 🥇 ${fmtTime(g)}`,
        },
        {
          txt: `${c.pack ? MEDAL_ICON[c.pack] : "○"} BEAT THE PACK`, has: !!c.pack,
          title: "win a VS race — 🥉 easy · 🥈 med · 🥇 hard",
        },
      ];
      missionRowEl.replaceChildren(
        ...items.map((it) => {
          const sp = document.createElement("span");
          sp.className = "mrow-item" + (it.has ? " has" : "");
          sp.title = it.title;
          sp.textContent = it.txt;
          return sp;
        }),
      );
    }

    // ---------- mode picker (start screen): time trial or race difficulty ----------
    const modeKey = "lapracer.diff";
    try {
      const saved = localStorage.getItem(modeKey);
      const idx = MODES.findIndex((d) => d.id === saved);
      if (idx >= 0) modeIdx = idx;
    } catch { /* ignore */ }
    const modeBtns = MODES.map((d, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "diff-btn";
      btn.textContent = d.name;
      btn.addEventListener("click", () => { if (S.phase === "start") setMode(i); });
      diffsEl.appendChild(btn);
      return btn;
    });
    function applyDiff() {
      const diff = raceDiff();
      allowedSpeeds = road.length && diff ? buildAllowedSpeeds(road, T.SEG_LEN, diff, T.AI_CORNER) : [];
      modeBtns.forEach((b, bi) => b.classList.toggle("active", bi === modeIdx));
    }
    function setMode(i: number) {
      modeIdx = ((i % MODES.length) + MODES.length) % MODES.length;
      try { localStorage.setItem(modeKey, MODES[modeIdx]!.id); } catch { /* ignore */ }
      initRivals();
      applyDiff();
    }

    // ---------- paint picker (start screen) ----------
    const paintsEl = q('[data-el="paints"]');
    const paintKey = "lapracer.paint";
    let paintIdx = 0;
    try {
      const saved = Number(localStorage.getItem(paintKey));
      if (Number.isInteger(saved) && saved >= 0 && saved < PAINTS.length) paintIdx = saved;
    } catch { /* ignore */ }
    const playerPaint = () => PAINTS[paintIdx]!.col;
    const paintBtns = PAINTS.map((p, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "paint-swatch";
      btn.title = p.name;
      btn.style.background = `rgb(${p.col[0]},${p.col[1]},${p.col[2]})`;
      btn.addEventListener("click", () => { if (S.phase === "start") setPaint(i); });
      paintsEl.appendChild(btn);
      return btn;
    });
    function setPaint(i: number) {
      paintIdx = ((i % PAINTS.length) + PAINTS.length) % PAINTS.length;
      try { localStorage.setItem(paintKey, String(paintIdx)); } catch { /* ignore */ }
      paintBtns.forEach((b, bi) => b.classList.toggle("active", bi === paintIdx));
    }
    setPaint(paintIdx);

    // ---------- dev tuning panel (` to toggle) ----------
    const tuningKey = "lapracer.tuning2"; // v2: the ×1.4 corner pace is baked into the AI formula
    try { // apply saved overrides
      let o = JSON.parse(localStorage.getItem(tuningKey) || "null") as Record<string, number> | null;
      if (!o) {
        // migrate v1 saves: their AI_CORNER was relative to the pre-bake formula, so a
        // stored 1.4 means "the pace that is now 1.0" — rescale, don't double-apply
        o = JSON.parse(localStorage.getItem("lapracer.tuning") || "{}") as Record<string, number>;
        if (typeof o.AI_CORNER === "number") o.AI_CORNER = Math.round((o.AI_CORNER / 1.4) * 50) / 50;
      }
      for (const kn of KNOBS) if (typeof o[kn.k] === "number") (T as Record<string, number>)[kn.k] = o[kn.k]!;
    } catch { /* ignore */ }
    const saveTuning = () => {
      try {
        const o: Record<string, number> = {};
        for (const kn of KNOBS) o[kn.k] = T[kn.k];
        localStorage.setItem(tuningKey, JSON.stringify(o));
      } catch { /* ignore */ }
    };
    const knobRefreshers: (() => void)[] = [];
    for (const kn of KNOBS) {
      const row = document.createElement("label");
      row.className = "dev-row";
      const lab = document.createElement("span");
      lab.textContent = kn.label;
      const inp = document.createElement("input");
      inp.type = "range";
      inp.min = String(kn.min); inp.max = String(kn.max); inp.step = String(kn.step);
      const val = document.createElement("span");
      val.className = "val";
      const refresh = () => { inp.value = String(T[kn.k]); val.textContent = String(T[kn.k]); };
      refresh();
      knobRefreshers.push(refresh);
      inp.addEventListener("input", () => {
        (T as Record<string, number>)[kn.k] = Number(inp.value);
        val.textContent = inp.value;
        saveTuning();
        if (kn.k === "AI_CORNER") applyDiff(); // braking map bakes this in — rebuild live
      });
      inp.addEventListener("change", () => inp.blur()); // give arrows back to steering
      row.append(lab, inp, val);
      devRowsEl.appendChild(row);
    }
    // sliders swallow their own keystrokes so tuning never fights the wheel
    const devKeys = (e: KeyboardEvent) => e.stopPropagation();
    devEl.addEventListener("keydown", devKeys);
    const onDevReset = () => {
      Object.assign(T, TUNING_DEFAULTS);
      try { localStorage.removeItem(tuningKey); localStorage.removeItem("lapracer.tuning"); } catch { /* ignore */ }
      knobRefreshers.forEach((fn) => fn());
    };
    const onDevResetRecords = () => {
      if (!window.confirm("Wipe ALL best laps, race times, and challenge medals?")) return;
      try {
        for (const tk of TRACKS) {
          localStorage.removeItem(bestKeyFor(tk.id));
          localStorage.removeItem(raceBestKeyFor(tk.id));
        }
        localStorage.removeItem("lapracer.best." + car.id); // legacy pre-track-select key
        localStorage.removeItem("lapracer.challenges");
      } catch { /* ignore */ }
      loadTrack(trackIdx); // re-read the (now empty) records into the UI
    };
    devResetBtn.addEventListener("click", onDevReset);
    devResetRecBtn.addEventListener("click", onDevResetRecords);

    // ---------- controls ----------
    const keys = new Set<string>();
    function startRace() {
      S.phase = "countdown";
      S.speed = 0; S.gear = 1; S.rpm = car.idle; S.position = 0; S.playerX = 0; S.steerVel = 0; S.driftVel = 0;
      S.gas = 0; S.brake = 0; S.throttle = 0; S.lapTime = 0; S.fuelCut = false;
      S.lap = 0; S.raceTime = 0; S.raceBest = 0;
      S.boost = 0; S.boosting = false; S.contacts = 0; S.offroadS = 0;
      bogUntil = 0;
      gapTargetName = ""; gapSampleT = 0; gapSampleV = 0; gapTrend = 0;
      shiftChanceUsed.clear();
      curSplits = []; lastSeg = -1; countdownStart = gameTime;
      initTraffic(); initRivals(); applyDiff();
      startEl.classList.add("hidden");
      resultsEl.classList.add("hidden");
      if (engine.on) { engine.ensure(); engine.resume(); }
    }
    // the FIRST upshift INTO each gear is special: nail it in the sweet spot (shift
    // light on, before the redline) and you're handed NOS. One chance per gear per race
    // — consumed even on a miss, no retries by downshifting. A perfect 1→2→3→4→5→6 run
    // up through the box banks five payouts. Skill-check the launch.
    const shiftChanceUsed = new Set<number>();
    function onUpshift(target: number) {
      if (S.phase !== "racing" || shiftChanceUsed.has(target)) return;
      shiftChanceUsed.add(target);
      if (S.rpm >= car.shift && S.rpm <= car.redline) {
        S.boost = Math.min(1, S.boost + T.BOOST_SHIFT);
        nosFlashUntil = gameTime + 1.2;
        // small toast above the dash — a center banner sat on the horizon and hid traffic
        toast("PERFECT SHIFT ★");
      }
    }
    function shift(dir: number) {
      const ng = clamp(S.gear + dir, 1, car.gears);
      if (ng > S.gear) onUpshift(ng);
      S.gear = ng;
    }
    function setGear(g: number) {
      const ng = clamp(g, 1, car.gears);
      if (ng > S.gear) onUpshift(ng);
      S.gear = ng;
    }
    function onKeyDown(e: KeyboardEvent) {
      const k = e.key.toLowerCase();
      if (["arrowup", "arrowdown", "arrowleft", "arrowright", " "].includes(k)) e.preventDefault();
      if (keys.has(k)) return;
      keys.add(k);
      if (S.phase === "start") { // menu navigation
        if (k === "arrowleft" || k === "a") { keys.delete(k); loadTrack(trackIdx - 1); return; }
        if (k === "arrowright" || k === "d") { keys.delete(k); loadTrack(trackIdx + 1); return; }
        if (k === "arrowup" || k === "arrowdown") { keys.delete(k); setMode(modeIdx + (k === "arrowdown" ? 1 : -1)); return; }
      }
      if (k === "enter" || k === "r" || (k === " " && S.phase === "start")) startRace();
      else if (k === "escape") { // back to the circuit-select screen
        S.phase = "start"; S.speed = 0;
        startEl.classList.remove("hidden");
        resultsEl.classList.add("hidden");
        bannerEl.className = "banner";
      }
      else if (k === "`") devEl.classList.toggle("open");
      else if (k === "m") toggleSound();
      else if (k === "e" || k === "x" || k === ".") shift(1);
      else if (k === "q" || k === "z" || k === ",") shift(-1);
      else if (k >= "1" && k <= "6") setGear(Number(k));
    }
    const onKeyUp = (e: KeyboardEvent) => keys.delete(e.key.toLowerCase());
    const onBlur = () => keys.clear();
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    const onSound = () => toggleSound();
    const onStart = () => startRace();
    soundBtn.addEventListener("click", onSound);
    startBtn.addEventListener("click", onStart);

    // ---------- physics ----------
    function physics(dt: number) {
      const gasT = keys.has("arrowup") || keys.has("w") ? 1 : 0;
      const brakeT = keys.has("arrowdown") || keys.has("s") ? 1 : 0;
      S.gas += (gasT - S.gas) * Math.min(1, dt * 8);
      S.brake += (brakeT - S.brake) * Math.min(1, dt * 10);
      S.throttle = S.gas;

      if (S.phase !== "racing") {
        // lazy flywheel while parked: revs CHASE the throttle instead of snapping to it,
        // so holding the launch zone is a rhythm skill, not a frame-perfect lift
        S.rpm += (car.idle + S.gas * 2500 - S.rpm) * Math.min(1, dt * 3.2);
        S.speed = 0;
        return;
      }

      const mps = S.speed / 3.6;
      const gt = ratio(S.gear) * car.final;

      const rpm = clamp(speedToRPM(S.speed, S.gear), 0, car.mechMax);
      S.fuelCut = rpm >= car.revLimit;
      // wheelspin after a botched launch: the tires are lit up, throttle does ~nothing
      const bogged = gameTime < bogUntil;
      const engineT = S.fuelCut ? 0 : S.throttle * car.maxTq * torqueAt(rpm) * (bogged ? 0.2 : 1);
      const driveForce = clamp((engineT * gt) / car.wheelR, -car.grip, car.grip);
      const engBrake = (1 - S.throttle) * (18 + rpm * 0.02) * Math.abs(gt) / car.wheelR;

      const offRoad = Math.abs(S.playerX) > 1;
      if (offRoad) S.offroadS += dt; // mission bookkeeping
      const resist = (Math.abs(mps) > 0.01 ? Math.sign(mps) : 0) * car.roll + car.aero * mps * Math.abs(mps);
      const brakeF = S.brake * car.brakeMax * (mps > 0.05 ? 1 : 0);
      const offDrag = offRoad && S.speed > T.OFFROAD_MAX_KMH ? T.OFFROAD_DECEL : 0;

      // NOS: hold shift to dump the tank — a straight shove of extra thrust
      S.boosting = keys.has("shift") && S.boost > 0 && S.gas > 0.2 && !offRoad;
      const boostF = S.boosting ? T.BOOST_FORCE : 0;
      if (S.boosting) S.boost = Math.max(0, S.boost - T.BOOST_DRAIN * dt);

      const net = driveForce + boostF - resist - brakeF - engBrake - offDrag;
      let newMps = mps + (net / car.mass) * dt;
      if (newMps < 0) newMps = 0;
      S.speed = newMps * 3.6;
      S.rpm = clamp(speedToRPM(S.speed, S.gear), car.idle * 0.4, car.mechMax);

      // steering + cornering. The car does NOT auto-follow the road: a corner pumps
      // outward drift MOMENTUM (v²/r — quadratic in speed), so the car tries to keep
      // going straight while the road bends away underneath it. Grip bleeds the drift
      // off once the road straightens, so exits carry a slide you must counter-steer.
      const speedPct = clamp(S.speed / 290, 0, 1);
      // sample the curve at the SPRITE's plane, not the camera — the camera trails the
      // visible car by ~5 segments, so sampling at the camera kept the corner force
      // firing after the car had visibly exited the turn
      const seg = road[Math.floor((S.position + PLAYER_Z) / T.SEG_LEN) % N]!;
      const steerIn = (keys.has("arrowleft") || keys.has("a") ? -1 : 0) + (keys.has("arrowright") || keys.has("d") ? 1 : 0);
      // slidey steering: input eases into a lateral velocity (loose, not on-rails)
      const steerTarget = steerIn * T.STEER * (0.45 + 0.55 * speedPct);
      S.steerVel += (steerTarget - S.steerVel) * Math.min(1, dt * T.STEER_SLIDE);
      S.driftVel -= seg.curve * speedPct * speedPct * T.CENTRIFUGAL * dt;
      // grip is road-aware: loose mid-corner (GRIP_RECOVER — the fight stays), but the
      // tires hook up fast once the road straightens under the car (GRIP_STRAIGHT), so
      // the slide stops carrying long past the exit
      const curveMag = Math.min(1, Math.abs(seg.curve) / 4);
      const recover = lerp(T.GRIP_STRAIGHT, T.GRIP_RECOVER, curveMag);
      S.driftVel -= S.driftVel * Math.min(1, dt * recover);
      S.playerX += (S.steerVel + S.driftVel) * dt;
      S.playerX = clamp(S.playerX, -3.4, 3.4);

      const before = S.position;
      S.position += S.speed * T.POS_K * dt;
      S.lapTime += dt;
      S.raceTime += dt;
      if (S.position >= trackLen) { S.position -= trackLen; completeLap(); }
      recordSplits(before, S.position);

      if (!Number.isFinite(S.speed)) S.speed = 0;
      if (!Number.isFinite(S.rpm)) S.rpm = car.idle;
    }

    function recordSplits(_before: number, after: number) {
      const seg = Math.floor(after / T.SEG_LEN) % N;
      if (seg === lastSeg) return;
      // stamp EVERY segment crossed since the last frame — at 200 mph that's 2-3 per
      // frame, and holes in the reference lap would blank the live delta next time
      let i = (lastSeg + 1 + N) % N;
      for (let guard = 0; guard < N; guard++) {
        curSplits[i] = S.lapTime;
        if (i === seg) break;
        i = (i + 1) % N;
      }
      lastSeg = seg;
    }
    function completeLap() {
      S.lap += 1;
      S.lastLap = S.lapTime;
      S.raceBest = S.raceBest ? Math.min(S.raceBest, S.lastLap) : S.lastLap;
      lastEl.textContent = fmtTime(S.lastLap);
      let newBest = false;
      if (!S.bestLap || S.lastLap < S.bestLap) {
        S.bestLap = S.lastLap;
        bestSplits = curSplits.slice();
        saveBest(S.bestLap, bestSplits);
        bestEl.textContent = fmtTime(S.bestLap);
        newBest = true;
        flashBanner("★ NEW BEST LAP  " + fmtTime(S.bestLap), "best");
      } else {
        flashBanner("LAP " + S.lap + "  " + fmtTime(S.lastLap), "info");
      }
      S.lapTime = 0; curSplits = []; lastSeg = -1;
      // both modes are the same timed race — TIME TRIAL is just the empty grid
      if (S.lap >= raceLaps()) finishRace(newBest);
    }
    function finishRace(newBest: boolean) {
      S.phase = "finished";
      const diff = raceDiff();
      const place = playerPlace();
      resPosRowEl.style.display = diff ? "" : "none"; // no POSITION row in time trial
      resPosEl.textContent = `P${place} / ${rivals.length + 1}`;
      resTotalEl.textContent = fmtTime(S.raceTime);
      resBestEl.textContent = fmtTime(S.raceBest);
      const notes: string[] = [];
      if (diff && place === 1) notes.push("★ YOU WIN ★");
      if (newBest) notes.push("★ NEW TRACK RECORD ★");
      resNoteEl.textContent = notes.join("  ·  ");
      // grade the challenges and show what this run earned (★ = first time / upgraded)
      const stats: RaceStats = {
        finished: true, raceTime: S.raceTime, contacts: S.contacts, offroadS: S.offroadS,
        won: !!diff && place === 1, diffId: diff ? diff.id : null,
      };
      saveRaceBest(S.raceTime);
      const earned = awardChallenges(track.id, track.medalLaps, raceLaps(), stats);
      resMedalsEl.textContent = earned.map((e) => e.label + (e.isNew ? " ★" : "")).join("   ");
      refreshMedalUI();
      resultsEl.classList.remove("hidden");
      bannerEl.className = "banner"; // don't flash a lap banner over the card
    }

    let bannerUntil = 0;
    function flashBanner(text: string, cls: string) {
      bannerEl.textContent = text;
      bannerEl.className = "banner show " + cls;
      bannerUntil = gameTime + 2.5;
    }

    // ---------- rendering: road ----------
    const rctx = roadCanvas.getContext("2d")!;
    const tctx = tachCanvas.getContext("2d")!;
    const mctx = mapCanvas.getContext("2d")!;
    let roadSize = { w: 1, h: 1 };
    let tachSize = { w: 1, h: 1 };
    let mapSize = { w: 1, h: 1 };
    function fit(cv: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      const r = cv.getBoundingClientRect();
      cv.width = Math.max(1, Math.round(r.width * dpr));
      cv.height = Math.max(1, Math.round(r.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w: r.width, h: r.height };
    }
    function resize() {
      roadSize = fit(roadCanvas!, rctx);
      tachSize = fit(tachCanvas!, tctx);
      mapSize = fit(mapCanvas!, mctx);
      buildMapPath();
    }
    window.addEventListener("resize", resize);

    function fog(color: Col, amount: number): string {
      const f = theme.fog;
      return `rgb(${Math.round(lerp(color[0], f[0], amount))},${Math.round(lerp(color[1], f[1], amount))},${Math.round(lerp(color[2], f[2], amount))})`;
    }

    function drawRoad() {
      const { w: W, h: H } = roadSize;
      const halfW = W / 2;
      const horizon = H * 0.42;

      // judder — the view rattles while plowing through grass, harder on an impact
      const shake = (Math.abs(S.playerX) > 1 && S.speed > 4 ? 7 : 0) + impactFlash * 13;
      rctx.save();
      if (shake > 0) rctx.translate((Math.random() - 0.5) * shake, (Math.random() - 0.5) * shake);

      const sky = rctx.createLinearGradient(0, 0, 0, horizon);
      sky.addColorStop(0, theme.sky[0]); sky.addColorStop(0.6, theme.sky[1]); sky.addColorStop(1, theme.sky[2]);
      rctx.fillStyle = sky; rctx.fillRect(0, 0, W, horizon);
      const sun = rctx.createRadialGradient(W * 0.68, horizon * 0.82, 3, W * 0.68, horizon * 0.82, W * 0.5);
      sun.addColorStop(0, "rgba(255,244,214,.95)"); sun.addColorStop(0.25, "rgba(255,224,168,.4)"); sun.addColorStop(1, "rgba(255,224,168,0)");
      rctx.fillStyle = sun; rctx.fillRect(0, 0, W, horizon);
      const ground = rctx.createLinearGradient(0, horizon, 0, H);
      ground.addColorStop(0, theme.ground[0]); ground.addColorStop(1, theme.ground[1]);
      rctx.fillStyle = ground; rctx.fillRect(0, horizon, W, H - horizon);

      const project = (worldX: number, camZ: number, cameraX: number) => {
        const scale = T.CAMERA_DEPTH / camZ;
        return {
          x: halfW + scale * (worldX - cameraX) * halfW,
          y: horizon + scale * T.CAMERA_HEIGHT * (H - horizon),
          w: scale * T.ROAD_WIDTH * halfW,
        };
      };

      const base = Math.floor(S.position / T.SEG_LEN);
      const basePercent = (S.position - base * T.SEG_LEN) / T.SEG_LEN;
      const cameraX = S.playerX * T.ROAD_WIDTH;
      let x = 0;
      let dx = -(road[base % N]!.curve * basePercent);
      let maxy = H;

      // pass 1 (near→far): project + accumulate curvature, keep only the visible rows
      type P = ReturnType<typeof project>;
      type Row = { seg: Segment; p1: P; p2: P; f: number };
      const rows: Row[] = [];
      const rowBySeg = new Map<number, Row>();
      for (let n = 0; n < T.DRAW_DISTANCE; n++) {
        const seg = road[(base + n) % N]!;
        const camZ1 = (base + n) * T.SEG_LEN - S.position;
        const camZ2 = (base + n + 1) * T.SEG_LEN - S.position;
        const p1 = project(x, camZ1, cameraX);
        const p2 = project(x + dx, camZ2, cameraX);
        x += dx; dx += seg.curve;
        if (camZ1 <= T.CAMERA_DEPTH || p2.y >= maxy || p2.y >= p1.y) continue;
        const f = 1 - Math.exp(-T.FOG_DENSITY * Math.pow(n / T.DRAW_DISTANCE, 1.6));
        const row = { seg, p1, p2, f };
        rows.push(row);
        rowBySeg.set(seg.index, row);
        maxy = p2.y;
      }

      // pass 2 (far→near): paint each segment's road THEN its trees, so nearer road
      // correctly covers billboards sitting beside a distant corner
      for (let i = rows.length - 1; i >= 0; i--) {
        const { seg, p1, p2, f } = rows[i]!;
        const dark = Math.floor(seg.index / T.RUMBLE_GROUP) % 2 === 0;
        rctx.fillStyle = fog(dark ? theme.grass[0] : theme.grass[1], f);
        rctx.fillRect(0, p2.y, W, p1.y - p2.y);
        quad(rctx, p1.x, p1.y, p1.w * 1.12, p2.x, p2.y, p2.w * 1.12, fog(dark ? [200, 16, 46] : [235, 235, 235], f));
        quad(rctx, p1.x, p1.y, p1.w, p2.x, p2.y, p2.w, fog(dark ? [60, 64, 70] : [66, 70, 76], f));
        if (seg.index < 3) checker(rctx, p1.x, p1.y, p1.w, p2.x, p2.y, p2.w);
        else if (dark) quad(rctx, p1.x, p1.y, p1.w * 0.03, p2.x, p2.y, p2.w * 0.03, fog([242, 190, 58], f));
        // stop drawing trees once they're deep in the haze — far trees bunch at the
        // vanishing point and can smear across the distant road; fog hides the cutoff
        const trees = f < 0.5 ? treesBySeg.get(seg.index) : undefined;
        if (trees) for (const ox of trees) {
          // hard guarantee: a hazard is never drawn within the road, whatever the stored value
          const oxs = (ox < 0 ? -1 : 1) * Math.max(2.4, Math.abs(ox));
          drawHazard(rctx, fog, track.hazards.kind, p1.x + oxs * p1.w, p1.y, p1.w, f, theme.tree);
        }
      }
      // traffic + rival sprites: placed by each car's TRUE position (interpolated inside
      // its segment) so the sprite is exactly where the hitbox is; drawn far→near. A car
      // whose row was culled is inside the near plane — by then it has already slid off
      // the bottom edge of the screen, which is also where the collision plane sits.
      const sprites: { x: number; y: number; w: number; f: number; c: Col; v: number }[] = [];
      const pushSprite = (pos: number, laneX: number, c: Col, v: number) => {
        const segI = Math.floor(pos / T.SEG_LEN) % N;
        let row = rowBySeg.get(segI);
        let fr = (pos % T.SEG_LEN) / T.SEG_LEN;
        if (!row) {
          // nearest rows flicker in/out of the cull (their on-screen span crosses the
          // bottom edge frame to frame) — extrapolate from the NEXT segment's row so a
          // car alongside you slides off smoothly instead of blinking
          row = rowBySeg.get((segI + 1) % N);
          if (!row) return;
          fr -= 1; // one segment nearer than the row → lerp extrapolates below it
        }
        const w = lerp(row.p1.w, row.p2.w, fr);
        const y = lerp(row.p1.y, row.p2.y, fr);
        if (y > H + 260) return; // fully off the bottom
        sprites.push({
          x: lerp(row.p1.x, row.p2.x, fr) + laneX * w,
          y, w, f: row.f, c, v,
        });
      };
      for (const t of traffic) pushSprite(t.pos, t.x, t.color, t.variant);
      for (const r of rivals) pushSprite(r.pos, r.x, r.color, 3);
      sprites.sort((a, b) => a.y - b.y);
      for (const sp of sprites) drawTrafficCar(rctx, fog, sp.x, sp.y, sp.w, sp.f, sp.c, sp.v);
      drawPlayerCar(rctx, W, H, S, playerPaint());
      // impact feedback: a red vignette that blooms in from the edges and fades — no popup
      if (impactFlash > 0) {
        const vg = rctx.createRadialGradient(W / 2, H * 0.55, H * 0.25, W / 2, H * 0.55, H);
        vg.addColorStop(0, "rgba(200,16,46,0)");
        vg.addColorStop(1, `rgba(200,16,46,${(impactFlash * 0.55).toFixed(3)})`);
        rctx.fillStyle = vg;
        rctx.fillRect(0, 0, W, H);
      }
      rctx.restore();
    }

    // ---------- rendering: tach ----------
    function drawTach() {
      const { w, h } = tachSize;
      tctx.clearRect(0, 0, w, h);
      const cx = w / 2, cy = h / 2, R = Math.min(w, h) / 2 - 4;
      const start = Math.PI * 0.75, sweep = Math.PI * 1.5;
      const maxDial = 8, redAt = car.redline / 1000;

      tctx.beginPath(); tctx.arc(cx, cy, R, 0, Math.PI * 2);
      const g = tctx.createRadialGradient(cx, cy - R * 0.3, R * 0.1, cx, cy, R);
      g.addColorStop(0, "#141922"); g.addColorStop(1, "#070a0d");
      tctx.fillStyle = g; tctx.fill();
      tctx.lineWidth = 2; tctx.strokeStyle = "#20262e"; tctx.stroke();

      tctx.beginPath();
      tctx.arc(cx, cy, R - 8, start + sweep * (redAt / maxDial), start + sweep, false);
      tctx.lineWidth = 4; tctx.strokeStyle = "rgba(224,47,38,.85)"; tctx.stroke();

      // countdown launch zones: hold the needle in the GREEN at GO for a perfect
      // launch; the red band past it is wheelspin city
      if (S.phase === "countdown") {
        const zone = (lo: number, hi: number, color: string) => {
          tctx.beginPath();
          tctx.arc(cx, cy, R - 8, start + sweep * (lo / 1000 / maxDial), start + sweep * (hi / 1000 / maxDial), false);
          tctx.lineWidth = 5; tctx.strokeStyle = color; tctx.stroke();
        };
        zone(T.LAUNCH_LO, T.LAUNCH_HI, "rgba(82,193,122,.95)");
        zone(T.LAUNCH_SPIN, 3600, "rgba(224,47,38,.95)");
      }

      for (let i = 0; i <= maxDial; i++) {
        const a = start + sweep * (i / maxDial);
        const isRed = i >= redAt;
        tctx.beginPath();
        tctx.moveTo(cx + Math.cos(a) * (R - 12), cy + Math.sin(a) * (R - 12));
        tctx.lineTo(cx + Math.cos(a) * (R - 24), cy + Math.sin(a) * (R - 24));
        tctx.lineWidth = 2.5; tctx.strokeStyle = isRed ? "#ff6a58" : "#ffab40"; tctx.stroke();
        tctx.fillStyle = isRed ? "#ff6a58" : "#d9cdb4";
        tctx.font = `600 ${Math.round(R * 0.16)}px ui-monospace, Menlo, monospace`;
        tctx.textAlign = "center"; tctx.textBaseline = "middle";
        tctx.fillText(String(i), cx + Math.cos(a) * (R - 36), cy + Math.sin(a) * (R - 36));
      }

      if (S.rpm > car.shift && S.gear < car.gears) {
        tctx.beginPath(); tctx.arc(cx, cy, R - 2, 0, Math.PI * 2);
        tctx.lineWidth = 3; tctx.strokeStyle = S.fuelCut ? "#e02f26" : "#ffab40"; tctx.stroke();
      }

      const na = start + sweep * (clamp(S.rpm / 1000, 0, maxDial) / maxDial);
      tctx.save();
      tctx.translate(cx, cy); tctx.rotate(na);
      tctx.beginPath();
      tctx.moveTo(-R * 0.12, 0); tctx.lineTo(R - 26, -2.5); tctx.lineTo(R - 22, 0); tctx.lineTo(R - 26, 2.5);
      tctx.closePath();
      tctx.fillStyle = "#ff5a36"; tctx.shadowColor = "rgba(255,90,54,.7)"; tctx.shadowBlur = 10; tctx.fill();
      tctx.restore();
      tctx.beginPath(); tctx.arc(cx, cy, R * 0.09, 0, Math.PI * 2);
      tctx.fillStyle = "#2a3038"; tctx.fill();
    }

    // ---------- rendering: minimap (closed circuit + all racers) ----------
    let mapPath: { x: number; y: number }[] = [];
    function buildMapPath() {
      mapPath = road.length ? tracePath(road, mapSize.w, mapSize.h, 12) : [];
    }
    function drawMap() {
      const { w, h } = mapSize;
      mctx.clearRect(0, 0, w, h);
      if (mapPath.length < 2) return;
      mctx.beginPath();
      mctx.moveTo(mapPath[0]!.x, mapPath[0]!.y);
      for (const p of mapPath) mctx.lineTo(p.x, p.y);
      mctx.closePath();
      mctx.lineJoin = "round"; mctx.lineWidth = 5; mctx.strokeStyle = "rgba(0,0,0,.35)"; mctx.stroke();
      mctx.lineWidth = 3; mctx.strokeStyle = "rgba(226,221,209,.65)"; mctx.stroke();
      mctx.fillStyle = "#f4f4f4"; mctx.beginPath(); mctx.arc(mapPath[0]!.x, mapPath[0]!.y, 2.5, 0, Math.PI * 2); mctx.fill();
      for (const r of rivals) {
        const rp = mapPath[Math.floor(r.pos / T.SEG_LEN) % N]!;
        mctx.fillStyle = `rgb(${r.color[0]},${r.color[1]},${r.color[2]})`;
        mctx.beginPath(); mctx.arc(rp.x, rp.y, 3, 0, Math.PI * 2); mctx.fill();
      }
      const p = mapPath[Math.floor(S.position / T.SEG_LEN) % N]!;
      const pc = playerPaint();
      mctx.fillStyle = `rgb(${pc[0]},${pc[1]},${pc[2]})`;
      mctx.shadowColor = `rgba(${pc[0]},${pc[1]},${pc[2]},.9)`; mctx.shadowBlur = 8;
      mctx.strokeStyle = "rgba(255,255,255,.9)"; mctx.lineWidth = 1.5;
      mctx.beginPath(); mctx.arc(p.x, p.y, 4.5, 0, Math.PI * 2); mctx.fill(); mctx.stroke(); mctx.shadowBlur = 0;
    }

    // ---------- audio ----------
    const engine = createEngineAudio(car);
    function toggleSound() {
      const on = engine.toggle();
      soundBtn.textContent = "Sound: " + (on ? "On" : "Off");
      soundBtn.classList.toggle("on", on);
    }

    // ---------- HUD ----------
    function syncDOM() {
      speedEl.textContent = String(Math.round(S.speed * 0.621371));
      rpmEl.textContent = String(Math.round(S.rpm));
      gearEl.textContent = String(S.gear);
      const inRace = S.phase === "racing" || S.phase === "countdown";
      const curLap = inRace ? Math.min(S.lap + 1, raceLaps()) : S.lap;
      lapNumEl.textContent = curLap + "/" + raceLaps();
      posEl.textContent = raceDiff()
        ? (inRace || S.phase === "finished" ? "P" + playerPlace() : "–")
        : "TT";
      fillNos.style.height = (S.boost * 100).toFixed(0) + "%";
      fillNos.classList.toggle("flash", gameTime < nosFlashUntil);

      // live challenge tracker: see a challenge die the moment you blow it, and watch
      // the clock on the next time medal tick down
      chalEl.classList.toggle("show", inRace);
      if (inRace) {
        const setCh = (el: HTMLElement, label: string, alive: boolean) => {
          el.textContent = (alive ? "✓ " : "✗ ") + label;
          el.className = "ch " + (alive ? "ok" : "dead");
        };
        setCh(chTarmacEl, "TARMAC", S.offroadS <= 0);
        setCh(chCleanEl, "CLEAN", S.contacts === 0);
        chWinEl.style.display = raceDiff() ? "" : "none";
        if (raceDiff()) setCh(chWinEl, "P1", playerPlace() === 1);
        const [g, sv, bz] = timeTargets(track.medalLaps, raceLaps());
        const next = S.raceTime <= g ? ["🥇", g] as const : S.raceTime <= sv ? ["🥈", sv] as const : S.raceTime <= bz ? ["🥉", bz] as const : null;
        chTimeEl.textContent = next ? `${next[0]} ${fmtTime(next[1] - S.raceTime)}` : "✗ TIME";
        chTimeEl.className = "ch " + (next ? "time" : "dead");
      }
      lapTimeEl.textContent = fmtTime(S.lapTime);
      fillGas.style.height = (S.gas * 100).toFixed(0) + "%";
      fillBrake.style.height = (S.brake * 100).toFixed(0) + "%";

      // ---- the top chip: mode-aware ----
      // VS race: gap to the next car in seconds ("am I gaining?"), trend-colored,
      //   with the record delta as a small second line when available.
      // Time trial: the classic live delta vs your best lap.
      // Both: only from lap 2 (lap 1 is a standing start) in TT; race gap shows always.
      const seg = Math.floor(S.position / T.SEG_LEN) % N;
      const bestRef = bestSplits && S.lap > 0 ? bestSplits[seg] : undefined;
      const bestDelta = typeof bestRef === "number" ? S.lapTime - bestRef : null;
      const fmtDelta = (d: number) => (d >= 0 ? "+" : "−") + Math.abs(d).toFixed(2);

      if (S.phase === "racing" && raceDiff() && rivals.length) {
        // nearest car up the road (or, when leading, the pursuer at my heels)
        const pt = playerTotal();
        // prefer the closest car AHEAD; only track a pursuer when nobody's ahead
        let target: Rival | null = null, gapD = 0;
        for (const r of rivals) {
          const d = r.lap * trackLen + r.pos - pt;
          const better = d > 0
            ? !target || gapD <= 0 || d < gapD
            : !target || (gapD <= 0 && d > gapD);
          if (better) { target = r; gapD = d; }
        }
        if (target) {
          const chasing = gapD > 0;
          const secs = Math.abs(gapD) / (Math.max(S.speed, 50) * T.POS_K);
          // favor = signed "good for the player" metric: shrinking chase gap or growing cushion
          const favor = chasing ? -secs : secs;
          if (target.name !== gapTargetName) {
            gapTargetName = target.name; gapSampleT = gameTime; gapSampleV = favor; gapTrend = 0;
          } else if (gameTime - gapSampleT > 0.6) {
            gapTrend = favor - gapSampleV;
            gapSampleT = gameTime; gapSampleV = favor;
          }
          deltaMainEl.textContent = `${chasing ? "▲" : "▼"} ${target.name} ${chasing ? "+" : "−"}${secs.toFixed(1)}`;
          deltaSubEl.textContent = bestDelta != null ? fmtDelta(bestDelta) + " vs best" : "";
          const cls = gapTrend > 0.02 ? "ahead" : gapTrend < -0.02 ? "behind" : "even";
          deltaEl.className = "delta show " + cls;
        } else {
          deltaEl.className = "delta";
        }
      } else if (S.phase === "racing" && bestDelta != null) {
        deltaMainEl.textContent = fmtDelta(bestDelta);
        deltaSubEl.textContent = "";
        deltaEl.className = "delta show " + (bestDelta <= 0 ? "ahead" : "behind");
      } else {
        deltaEl.className = "delta";
      }
      if (bannerEl.classList.contains("show") && gameTime > bannerUntil) bannerEl.className = "banner";
      if (shiftToastEl.classList.contains("show") && gameTime > shiftToastUntil) shiftToastEl.classList.remove("show");
    }

    // ---------- main loop ----------
    let gameTime = 0;
    let countdownStart = 0;
    let raf = 0;
    let last: number | null = null;
    function frame(ts: number) {
      if (last === null) last = ts;
      let dt = (ts - last) / 1000; last = ts;
      dt = Math.min(dt, 0.05);
      gameTime += dt;

      if (impactFlash > 0) impactFlash = Math.max(0, impactFlash - dt * 2.2);
      if (S.phase === "countdown") {
        const remain = 3 - (gameTime - countdownStart);
        if (remain <= 0) {
          S.phase = "racing"; S.lapTime = 0; lastSeg = -1; curSplits = [];
          // revs held at GO carry over as a launch — with a goldilocks zone (shown green
          // on the tach during the countdown). In the zone: perfect launch. Pinned past
          // it: wheelspin — you crawl away with a bogged throttle. Below: partial carry.
          const rpm = S.rpm;
          if (rpm >= T.LAUNCH_LO && rpm <= T.LAUNCH_HI) {
            S.speed = 30;
            toast("PERFECT LAUNCH ★");
          } else if (rpm > T.LAUNCH_SPIN) {
            S.speed = 4;
            bogUntil = gameTime + 0.65;
            toast("WHEELSPIN!", true);
          } else {
            S.speed = clamp((rpm - car.idle) / 2500, 0, 1) * 24;
          }
          // rivals launch too — quality varies per start, better drivers nail it more
          const diff = raceDiff();
          for (const r of rivals) r.speed = 8 + Math.random() * 10 + (diff ? diff.accelMul * 10 : 0);
          flashBanner("GO!", "go");
        }
        else flashBanner(String(Math.ceil(remain)), "count");
        physics(dt);
      } else if (S.phase === "racing") {
        const steps = 5, sdt = dt / steps;
        for (let i = 0; i < steps; i++) physics(sdt);
        updateTraffic(dt);
        runRivals(dt);
      } else {
        physics(dt); // start screen / results — idle
        if (S.phase === "finished") updateTraffic(dt); // traffic keeps cruising behind the card
      }

      engine.update(S.rpm, S.throttle);
      drawRoad();
      drawTach();
      drawMap();
      syncDOM();
      raf = requestAnimationFrame(frame);
    }

    loadTrack(0);
    resize();
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("resize", resize);
      soundBtn.removeEventListener("click", onSound);
      startBtn.removeEventListener("click", onStart);
      devResetBtn.removeEventListener("click", onDevReset);
      devResetRecBtn.removeEventListener("click", onDevResetRecords);
      devEl.removeEventListener("keydown", devKeys);
      // strict-mode double-mount would duplicate the imperatively-built rows/cards
      tracksEl.replaceChildren();
      devRowsEl.replaceChildren();
      diffsEl.replaceChildren();
      paintsEl.replaceChildren();
      engine.dispose();
    };
  }, []);

  return (
    <div className="lap" ref={rootRef}>
      <style>{CSS}</style>

      <div className="lap-stage">
        <div className="lap-top">
          <div className="lap-brand">
            <h1>APEX<span className="dot">·</span></h1>
            <span className="tag">beat the pack · beat the clock</span>
          </div>
          <button className="sound-btn" type="button" onClick={toggleFullscreen}>
            {fullscreen ? "Exit fullscreen" : "Fullscreen"}
          </button>
          <button className="sound-btn on" data-el="soundBtn" type="button">Sound: On</button>
        </div>

        <div className="road-wrap" ref={wrapRef}>
          <button className="sound-btn fs-exit" type="button" onClick={toggleFullscreen}>
            Exit fullscreen (F)
          </button>
          <canvas className="road" ref={roadRef} />
          <div className="crt" />

          <div className="hud-timing">
            <div className="lap-line big"><span className="k">LAP</span><span className="v" data-el="lapTime">0:00.000</span></div>
            <div className="lap-line"><span className="k">BEST</span><span className="v best" data-el="best">--:--.---</span></div>
            <div className="lap-line"><span className="k">LAST</span><span className="v" data-el="last">--:--.---</span></div>
          </div>

          <div className="delta" data-el="delta">
            <span data-el="deltaMain">+0.00</span>
            <span className="delta-sub" data-el="deltaSub" />
          </div>

          <div className="hud-chal" data-el="chal">
            <span className="ch" data-el="chTarmac" />
            <span className="ch" data-el="chClean" />
            <span className="ch" data-el="chWin" />
            <span className="ch time" data-el="chTime" />
          </div>

          <div className="hud-map">
            <canvas ref={mapRef} />
            <span className="lapcount">Lap <b data-el="lapNum">0/4</b> · <b className="pos" data-el="pos">–</b></span>
          </div>

          <div className="hud-dash">
            <div className="tach-box"><canvas className="tach" ref={tachRef} /></div>
            <div className="readout">
              <div className="gear" data-el="gear">1</div>
              <div className="rpm"><span data-el="rpm">900</span><i>rpm</i></div>
              <div className="mph"><span data-el="speed">0</span><i>mph</i></div>
            </div>
            <div className="pedals">
              <div className="pedal"><div className="fill gas" data-el="fillGas" /><span>GAS</span></div>
              <div className="pedal"><div className="fill brake" data-el="fillBrake" /><span>BRK</span></div>
              <div className="pedal"><div className="fill nos" data-el="fillNos" /><span>NOS</span></div>
            </div>
          </div>

          <div className="banner" data-el="banner" />

          {/* dev tuning panel (`) */}
          <div className="dev" data-el="dev">
            <h3>TUNING</h3>
            <div className="dev-rows" data-el="devRows" />
            <div className="dev-btns">
              <button className="dev-reset" data-el="devReset" type="button">reset defaults</button>
              <button className="dev-reset" data-el="devResetRecords" type="button">reset records</button>
            </div>
          </div>

          <div className="shift-toast" data-el="shiftToast" />

          {/* race results */}
          <div className="results hidden" data-el="results">
            <div className="results-card">
              <div className="results-title">FINISH</div>
              <div className="rrow" data-el="resPosRow"><span>POSITION</span><b data-el="resPos">–</b></div>
              <div className="rrow"><span>RACE TIME</span><b data-el="resTotal">--:--.---</b></div>
              <div className="rrow"><span>BEST LAP</span><b data-el="resBest">--:--.---</b></div>
              <div className="results-medals" data-el="resMedals" />
              <div className="results-note" data-el="resNote" />
              <div className="results-hint">ENTER race again · ESC circuits</div>
            </div>
          </div>

          {/* retro California start screen */}
          <div className="start-screen" data-el="start">
            <div className="start-grid" />
            <div className="start-sun" />
            <div className="start-inner">
              <div className="start-kicker">CHAMPIONSHIP</div>
              <h2 className="start-title">APEX</h2>
              <div className="start-sub">outrun the pack · set the record</div>
              <div className="track-picker" data-el="tracks" />
              <div className="diff-picker" data-el="diffs" />
              <div className="paint-picker" data-el="paints" />
              <div className="mission-row" data-el="missionRow" />
              <div className="track-hint">◂ ▸ circuit · ▲ ▾ mode</div>
              <button className="start-btn" data-el="startBtn" type="button">▸ PRESS ENTER TO START</button>
            </div>
          </div>
        </div>

        <div className="lap-controls">
          <span><b>←/→</b> steer</span>
          <span><b>↑</b> gas</span>
          <span><b>↓</b> brake</span>
          <span><b>. / E</b> up-shift</span>
          <span><b>, / Q</b> down-shift</span>
          <span><b>Shift</b> NOS (fill it with near-misses)</span>
          <span><b>1–6</b> gear</span>
          <span><b>Enter</b> restart</span>
          <span><b>Esc</b> circuits</span>
          <span><b>M</b> mute</span>
          <span><b>F</b> fullscreen</span>
        </div>
      </div>
    </div>
  );
}
